import React, { useEffect, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import Tabs, { TabProps } from '@components/ringil3/RingilTabs/Tabs.tsx';
import SvgTabInternalInfo from '@components/ringil3/icons/TabInternalInfo.tsx';
import SvgTabAttachments from '@components/ringil3/icons/TabAttachments.tsx';
import SvgTabMessages from '@components/ringil3/icons/TabMessages.tsx';
import SvgTabExpedition from '@components/ringil3/icons/TabExpedition.tsx';
import SvgGlobe from '@icons/Globe.tsx';
import SvgIconFile from '@components/framework/icons/IconFile.tsx';
import { isNotNullOrUndefined } from '@utils/utils.tsx';

interface RingilTabsVisibilityProps {
  showShipment?: boolean;
  showCustoms?: boolean;
  showInternalInfo?: boolean;
  showCmr?: boolean;
  showMessages?: boolean;
  showAttachments?: boolean;
}

interface RenderProps {
  onMessagesClick: () => void;
}

export type RingilExpeditionTab = 'expedition' | 'internalInfoAndHistory' | 'attachments' | 'customs' | 'cmr' | 'messages';

/**
 * The expedition detail's tabs. Each one switches the pane below (no scroll-spy sections, unlike SN); the
 * strip itself is the shared `Tabs`, so tabs that don't fit — the expedition list's split pane — fold into "…".
 *
 * Hands out a render function rather than a component: a component made inside the hook would be a new type
 * on every render and remount the strip each time.
 */
export const useCreateRingilExpeditionTabs = ({ showShipment, showCustoms, showInternalInfo, showCmr, showMessages, showAttachments }: RingilTabsVisibilityProps) => {
  const { tr } = useTranslationLgs();
  const permittedTabs = [
    showShipment ? 'expedition' : null,
    showCustoms ? 'customs' : null,
    showInternalInfo ? 'internalInfoAndHistory' : null,
    showCmr ? 'cmr' : null,
    showAttachments ? 'attachments' : null,
  ].filter(t => isNotNullOrUndefined(t));

  const initialTab = permittedTabs.length > 0 ? permittedTabs[0] : null;

  useEffect(() => {
    if (initialTab) {
      // @ts-ignore
      setSelectedPanel(initialTab);
    }
  }, [initialTab]);

  // @ts-ignore
  const [selectedPanel, setSelectedPanel] = useState<RingilExpeditionTab>(initialTab);

  const allPanes: { pane: RingilExpeditionTab; shown: boolean; tab: Omit<TabProps, 'active'> }[] = [
    { pane: 'expedition', shown: showShipment, tab: { icon: <SvgTabExpedition />, label: tr(`RingilTabs.expedition`, `Expedition`) } },
    { pane: 'internalInfoAndHistory', shown: showInternalInfo, tab: { icon: <SvgTabInternalInfo />, label: tr(`RingilTabs.internalInfo`, `Internal info and history`) } },
    { pane: 'customs', shown: showCustoms, tab: { icon: <SvgGlobe />, label: tr(`RingilTabs.customs`, `Clení`) } },
    { pane: 'cmr', shown: showCmr, tab: { icon: <SvgIconFile />, label: tr(`RingilTabs.cmr`, `CMR`) } },
    { pane: 'attachments', shown: showAttachments, tab: { icon: <SvgTabAttachments />, label: tr(`RingilTabs.attachments`, `Attachments`) } },
    // A trigger, not a pane: it opens the chat and is never the selected tab.
    { pane: 'messages', shown: showMessages, tab: { icon: <SvgTabMessages />, label: tr(`RingilTabs.messages`, `Messages`) } },
  ];
  const panes = allPanes.filter(entry => entry.shown);

  const renderTabs = ({ onMessagesClick }: RenderProps) => (
    <Tabs
      className="mb-2"
      tabs={panes.map(({ pane, tab }) => ({ ...tab, active: pane !== 'messages' && selectedPanel === pane, dataTestId: `ringilTab-${pane}` }))}
      onTabChange={index => {
        const { pane } = panes[index];
        if (pane === 'messages') onMessagesClick();
        else setSelectedPanel(pane);
      }}
    />
  );

  return { renderTabs, selectedPanel };
};
